import React, { useCallback, useEffect, useState } from 'react'
import { CAlert, CBadge, CButton, CProgress, CSmartTable } from '@coreui/react-pro'
import CIcon from '@coreui/icons-react'
import { cilReload, cilWarning } from '@coreui/icons'
import PlayerService from 'src/service/PlayerService'
import ToastComponent from 'src/components/common/TaostComponent'
import TableSearchToolbar from 'src/components/common/TableSearchToolbar'
import { InfoBox } from 'src/components/common/ImportWizard'

// The userTrade endpoint only filters on full_name.
const SEARCH_FIELDS = [{ key: 'full_name', label: 'Member' }]

const columns = [
  { key: 'full_name', label: 'Member', filter: false },
  { key: 'totalTrades', label: 'Trades Used', filter: false, _style: { width: '35%' } },
  { key: 'trade_left', label: 'Trades Left', filter: false, _style: { width: '15%' } },
]

// Under a quarter of the allowance left is worth flagging before it runs out.
const tradesLeftColor = (left, allowance) => {
  if (left <= 0) return 'danger'
  if (allowance > 0 && left / allowance <= 0.25) return 'warning'
  return 'success'
}

const UserTrade = ({ onTotalChange, onAllowanceChange }) => {
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(false)
  const [activePage, setActivePage] = useState(1)
  const [searchField, setSearchField] = useState(SEARCH_FIELDS[0].key)
  const [searchValue, setSearchValue] = useState('')
  const [columnSorter, setColumnSorter] = useState(null)
  const [itemsPerPage, setItemsPerPage] = useState(20)
  const [users, setUsers] = useState([])

  const fetchUsers = useCallback(() => {
    setLoading(true)
    setError(false)
    const offset = itemsPerPage * activePage - itemsPerPage
    const term = searchValue.trim()
    let params = new URLSearchParams()
    term && params.append(searchField, term)
    columnSorter &&
      columnSorter.column !== undefined &&
      params.append('sort', `${columnSorter.column}%${columnSorter.state}`)

    PlayerService.userTrade(offset, itemsPerPage, activePage, params)
      .then((result) => {
        setUsers(result.data)
        // Only an unfiltered total describes the game; a searched one would shrink the tab count.
        if (!term) onTotalChange?.(result.data?.total ?? 0)
        // Every row carries the game's allowance, so any row tells us what it is.
        const allowance = result.data?.data?.[0]?.trades
        if (allowance !== undefined) onAllowanceChange?.(allowance)
      })
      .catch(() => {
        setError(true)
        ToastComponent('Could not load trade usage', 'error')
      })
      .finally(() => setLoading(false))
  }, [
    activePage,
    searchField,
    searchValue,
    columnSorter,
    itemsPerPage,
    onTotalChange,
    onAllowanceChange,
  ])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const noItemsLabel = searchValue.trim()
    ? 'No members match your search'
    : 'No members have a saved team yet'

  return (
    <>
      <InfoBox>
        Trades left is the game&apos;s trade allowance minus the trades a member has made. Only
        members with a saved team are listed.
      </InfoBox>

      {error ? (
        <CAlert color="danger" className="d-flex flex-wrap align-items-center gap-2 mb-0">
          <CIcon icon={cilWarning} className="flex-shrink-0" />
          <span>We couldn&apos;t load trade usage.</span>
          <CButton
            color="danger"
            variant="outline"
            size="sm"
            className="ms-auto"
            onClick={fetchUsers}
          >
            <CIcon icon={cilReload} className="me-1" />
            Retry
          </CButton>
        </CAlert>
      ) : (
        <>
          <TableSearchToolbar
            fields={SEARCH_FIELDS}
            field={searchField}
            onFieldChange={(field) => {
              setSearchField(field)
              setSearchValue('')
              setActivePage(1)
            }}
            value={searchValue}
            onSearch={(value) => {
              setActivePage(1)
              setSearchValue(value)
            }}
            placeholder="Search members..."
          />

          <div className="ge-datatable ge-datatable--no-select ge-datatable--static">
            <CSmartTable
              columns={columns}
              columnSorter={{ external: true }}
              scopedColumns={{
                full_name: (item) => (
                  <td>
                    <div className="tr-member-name">{item.full_name || 'Unnamed member'}</div>
                    {item.email && <div className="tr-member-meta">{item.email}</div>}
                  </td>
                ),
                totalTrades: (item) => {
                  const used = Number(item.totalTrades) || 0
                  const total = Number(item.trades) || 0
                  return (
                    <td>
                      <div className="tr-usage">
                        <span className="tr-usage-count">
                          {used}
                          {total > 0 && <span className="tr-usage-total"> / {total}</span>}
                        </span>
                        {total > 0 && (
                          <CProgress
                            thin
                            className="tr-usage-bar"
                            value={Math.min(100, (used / total) * 100)}
                            color={tradesLeftColor(total - used, total)}
                          />
                        )}
                      </div>
                    </td>
                  )
                },
                trade_left: (item) => {
                  const left = Number(item.trade_left) || 0
                  return (
                    <td>
                      <CBadge
                        shape="rounded-pill"
                        color={tradesLeftColor(left, Number(item.trades) || 0)}
                      >
                        {left} left
                      </CBadge>
                    </td>
                  )
                },
              }}
              items={users?.data}
              itemsPerPage={itemsPerPage}
              itemsPerPageSelect
              loading={loading}
              noItemsLabel={noItemsLabel}
              pagination={{ external: true }}
              paginationProps={{
                activePage: activePage,
                pages: Math.ceil(users?.total / itemsPerPage) || 1,
              }}
              tableProps={{ hover: true, responsive: true, className: 'align-middle mb-0' }}
              onActivePageChange={(activePage) => setActivePage(activePage)}
              onItemsPerPageChange={(itemsPerPage) => {
                setActivePage(1)
                setItemsPerPage(itemsPerPage)
              }}
              onSorterChange={(sorter) => setColumnSorter(sorter)}
            />
          </div>
        </>
      )}
    </>
  )
}

export default UserTrade
